import { Box } from '@mui/material'
import React, { useEffect, useMemo, useRef } from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import { ErrorFallback } from '@/shared/ui/ErrorFallback'
import useEnterPageLogAnalytics from '@/shared/hooks/useEnterPageLogAnalytics'
import ScrollToTopButton from '@/components/tv/components/ScrollToTopButton'
import { useVODStore } from '@/shared/store'
import { pageType } from '@/shared/store/types'
import { useAtmospheric } from '@/features/VOD/hooks/useAtmospheric'
import { VodRowViewProps } from '@/types/vod/vodRowType'
import VodRowView from '@/features/VOD/components/VodRowView'
import {
    vodItemImageHeight,
    VodPageDetailsType,
    vodZeroRowItemImageHeight,
    ZERO_ROW,
} from '@/shared/constants'
import { VodItemsProvider } from '@/features/VOD/context/UseGetVodItemContext/VodItemsContext'
import ZeroRowView from '@/clients/streamiq/features/VOD/components/ZeroRowView'
import { useSeriesScreen } from '@/clients/streamiq/pages/hooks/useSeriesScreenHook'
import { homeRowId } from '@/shared/constants/seriesConstants'

export const SeriesRowContainer = () => {
    const homeContainerRef = useRef<HTMLDivElement | null>(null)
    useEnterPageLogAnalytics(homeRowId)

    const containerStyle = {
        height: '90vh',
        overflowY: 'auto',
        display: 'flex',
        gap: '50px',
        flexDirection: 'column',
        // padding: '30px',
        '& > :first-of-type': {
            // marginBottom: '-50px', // hack to reduce flex gap on first element
        },
    }

    const scrollToTop = () => {
        const container = homeContainerRef.current
        if (container)
            container?.scrollTo({
                top: 0,
                behavior: 'smooth',
            })
    }
    const seriesRowsRef = useRef<HTMLDivElement[]>([])
    const { seriesRows } = useSeriesScreen()
    const { setVodPages } = useVODStore()

    const handleGoToCategoryButtonClick = (
        itemId: string,
        title: string,
        type: pageType
    ): void => {
        setVodPages(itemId, title, type)
        if (type !== VodPageDetailsType) {
            homeContainerRef.current?.scrollTo(0, 0)
        }
    }

    const { rowElements, handleScroll, handleRefsUpdate } = useAtmospheric(
        homeContainerRef,
        seriesRows || []
    )

    useEffect(() => {
        handleRefsUpdate(seriesRowsRef.current)
    }, [seriesRows?.length, handleRefsUpdate, seriesRowsRef.current])

    useEffect(() => {
        const container = homeContainerRef.current
        if (container) {
            container.addEventListener('scroll', handleScroll)
        }

        return () => {
            if (container) {
                container.removeEventListener('scroll', handleScroll)
            }
        }
    }, [rowElements, homeContainerRef.current])

    const ZeroRow = useMemo(() => {
        const zeroRow = seriesRows.find(
            (row: VodRowViewProps) => row.title.toLowerCase() === ZERO_ROW
        )
        if (!zeroRow) return <></>

        const {
            productId,
            query,
            serviceName,
            hideCaptions,
            hideProgress,
            rowHeightPercentage,
        } = zeroRow

        return (
            <Box
                key={productId}
                data-index={productId}
                ref={(listElement: HTMLDivElement) => {
                    seriesRowsRef.current[1] = listElement
                }}>
                <VodItemsProvider
                    productId={String(productId)}
                    type={query || ''}
                    serviceName={serviceName || ''}>
                    <ZeroRowView
                        productId={productId}
                        hideCaptions={hideCaptions}
                        hideProgress={hideProgress}
                        rowHeightPercentage={rowHeightPercentage}
                        maxHeight={vodZeroRowItemImageHeight}
                        handleGoToCategoryButtonClick={
                            handleGoToCategoryButtonClick
                        }
                        isHomeContentRow={false}
                    />
                </VodItemsProvider>
            </Box>
        )
    }, [seriesRows, handleGoToCategoryButtonClick])

    const contentToDisplay = useMemo(() => {
        if (!seriesRows) return null

        return seriesRows.map((row: VodRowViewProps, index: number) => {
            const {
                productId,
                title,
                hideCaptions,
                hideProgress,
                rowHeightPercentage,
                hideTitle,
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                serviceName,
            } = row

            if (title.toLowerCase() !== ZERO_ROW)
                return (
                    <Box
                        key={productId}
                        data-index={row.productId}
                        ref={(listElement: HTMLDivElement) => {
                            seriesRowsRef.current[index] = listElement
                        }}>
                        <VodItemsProvider
                            productId={String(row.productId)}
                            type={row.query || ''}
                            serviceName={row.serviceName || ''}>
                            <VodRowView
                                productId={productId}
                                title={title}
                                hideTitle={hideTitle}
                                hideCaptions={hideCaptions}
                                hideProgress={hideProgress}
                                rowHeightPercentage={rowHeightPercentage}
                                maxHeight={vodItemImageHeight}
                                handleGoToCategoryButtonClick={
                                    handleGoToCategoryButtonClick
                                }
                                isHomeContentRow={false}
                            />
                        </VodItemsProvider>
                    </Box>
                )
        })
    }, [seriesRows, handleGoToCategoryButtonClick])

    return (
        <ErrorBoundary FallbackComponent={ErrorFallback}>
            <Box sx={containerStyle} ref={homeContainerRef}>
                {ZeroRow}

                {/*<Container maxWidth="xl" disableGutters>*/}
                <Box sx={{ padding: '0 30px 0 30px' }}>{contentToDisplay}</Box>
                {/*</Container>*/}

                <ScrollToTopButton
                    handleOnClick={scrollToTop}
                    container={homeContainerRef.current}
                />
            </Box>
        </ErrorBoundary>
    )
}
